import { useState } from "react";
import { Check, Crown, Loader2, Sparkles, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { useAuth } from "@/hooks/useAuth";
import { PREMIUM_BENEFITS } from "@/lib/premium";
import { TRIAL_CANCEL_NOTE, TRIAL_CTA, TRIAL_DISCLOSURE } from "@/lib/trial";
import { useTrialEligibility } from "@/hooks/useTrialEligibility";
import { TrialConfirmDialog } from "@/components/TrialConfirmDialog";
import { canFallBackToWebCheckout, isStoreManagedBilling, mustUseStoreBilling, nativePlatform, storeLabel } from "@/lib/store-billing";
import { purchasePremiumViaStore, restoreStorePurchases } from "@/lib/native-billing";
import { useStorePrices } from "@/hooks/useStorePrices";
import { StoreSubscriptionTerms } from "@/components/StoreSubscriptionTerms";

export type UpgradeReason = "scan_limit" | "locked_feature" | "trial_ended" | "generic";

const COPY: Record<UpgradeReason, { title: string; body: string }> = {
  scan_limit: {
    title: "You've used today's study allowance",
    body: "Premium adds guided tutoring and full study access. Compare the daily allowances before subscribing.",
  },
  locked_feature: {
    title: "This is a Premium study tool",
    body: "Unlock your AI Coach, exam plans, flashcards, quizzes and guided tutoring.",
  },
  trial_ended: {
    title: "Your Premium trial has ended",
    body: "Keep Premium tutoring, your AI Coach and your streak going.",
  },
  generic: {
    title: "Go Premium",
    body: "Premium AI study tools for $9.99/month. The proposed annual plan is $79.99 and is not available yet.",
  },
};

interface Props {
  reason: UpgradeReason;
  /** Name of the feature that triggered the prompt (analytics only). */
  feature?: string;
  onClose: () => void;
}

/**
 * Natural upgrade prompt shown when someone hits the daily scan limit or opens
 * a locked Premium feature. Offers the 3-day free trial when the account is
 * still eligible, otherwise routes to checkout (Stripe on web, the platform
 * store inside the mobile app).
 */
export function UpgradePromptModal({ reason, feature, onClose }: Props) {
  const { t } = useTranslation();
  const { premium, refreshPremium } = useAuth();
  const [busy, setBusy] = useState<"trial" | "store" | "restore" | null>(null);
  const [confirmingTrial, setConfirmingTrial] = useState(false);
  const { eligible: trialEligible } = useTrialEligibility(premium);
  const native = mustUseStoreBilling();
  const storeManaged = isStoreManagedBilling();
  const platform = nativePlatform();
  const storePrices = useStorePrices();
  const monthlyPrice = storePrices.monthly ?? "$9.99";
  const copy = COPY[reason];

  const buyInStore = async () => {
    setBusy("store");
    try {
      void track("upgrade_button_clicked", { surface: `prompt_${reason}`, channel: platform ?? "store", feature });
      const result = await purchasePremiumViaStore("monthly");
      if (!result.ok) {
        // Missing billing bridge outside iOS: send the customer to web checkout
        // instead of dead-ending the upgrade prompt.
        if (
          (result.error === "no_bridge" || result.error === "store_not_configured") &&
          canFallBackToWebCheckout()
        ) {
          void track("checkout_fallback_web", { surface: `prompt_${reason}`, reason: result.error });
          onClose();
          window.location.href = "/upgrade";
          return;
        }
        if (result.error !== "cancelled") {
          toast.error(
            result.error === "pending"
              ? "Your purchase is pending approval. Premium unlocks once it's approved."
              : result.error === "already_owned"
                ? "That subscription is already active — tap Restore Purchases."
                : result.error === "store_not_configured" ||
                    result.error === "no_bridge" ||
                    result.error === "product_unavailable"
                  ? `We couldn't reach the ${storeLabel(platform)}. Please check your connection and try again.`
                  : "Purchase couldn't be completed.",
          );
        }
        return;
      }
      toast.success("Premium activated. Enjoy!");
      await refreshPremium();
      onClose();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="modal-overlay fixed inset-0 z-[120] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
    >
      <div className="modal-panel w-full max-w-md rounded-3xl border border-rule bg-card shadow-2xl animate-in fade-in slide-in-from-bottom-4">
        <div className="relative bg-gradient-to-br from-neon-pink via-purple-600 to-neon-blue px-6 pb-6 pt-5 text-center text-white">
          <button
            onClick={onClose}
            aria-label="Close upgrade prompt"
            className="absolute right-4 top-4 text-white/80 transition hover:text-white"
          >
            <X className="size-5" />
          </button>
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-white/20 ring-2 ring-neon-yellow/70">
            <Crown className="size-6 text-neon-yellow" />
          </div>
          <h2 className="mt-3 font-[family-name:var(--font-display)] text-xl font-extrabold">{copy.title}</h2>
          <p className="mt-1 text-sm text-white/90">{copy.body}</p>
        </div>

        <div className="space-y-4 p-5">
          <ul className="grid gap-1.5">
            {PREMIUM_BENEFITS.slice(0, 5).map((benefit) => (
              <li key={benefit} className="flex items-center gap-2 text-sm text-ink">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-neon-yellow/25">
                  <Check className="size-3.5 text-neon-pink" />
                </span>
                {benefit}
              </li>
            ))}
          </ul>

          <div className="space-y-2">
            {trialEligible && (
              <>
                <Button
                  variant="scan"
                  size="full"
                  disabled={busy !== null}
                  onClick={() => setConfirmingTrial(true)}
                  className="font-[family-name:var(--font-display)]"
                >
                  <Sparkles className="size-5" /> {TRIAL_CTA}
                </Button>
                <p className="text-center text-xs font-semibold text-ink">{TRIAL_DISCLOSURE}</p>
                <p className="text-center text-[11px] font-semibold text-ink">{TRIAL_CANCEL_NOTE}</p>
              </>
            )}

            {native ? (
              <Button
                variant={trialEligible ? "outline" : "scan"}
                size="full"
                disabled={busy !== null}
                onClick={() => void buyInStore()}
              >
                {busy === "store" ? <Loader2 className="size-5 animate-spin" /> : <Crown className="size-5" />}
                {busy === "store" ? "Opening App Store…" : `Subscribe with ${storeLabel(platform)} — ${monthlyPrice}/mo`}
              </Button>
            ) : (
              <Link to="/upgrade" onClick={onClose} className="block">
                <Button variant={trialEligible ? "outline" : "scan"} size="full">
                  <Crown className="size-5" /> {trialEligible ? "See Premium plans" : `Get Premium — ${monthlyPrice}/mo`}
                </Button>
              </Link>
            )}

            {storeManaged && (
              <Button
                variant="subtle"
                size="full"
                disabled={busy !== null}
                onClick={async () => {
                  setBusy("restore");
                  try {
                    const result = await restoreStorePurchases();
                    await refreshPremium();
                    if (result.premium) {
                      toast.success(t("store.restored"));
                      onClose();
                    } else {
                      toast.info(t("store.noSubscriptionFound"));
                    }
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                {busy === "restore" ? <Loader2 className="size-5 animate-spin" /> : null} Restore Purchases
              </Button>
            )}

            <StoreSubscriptionTerms plan="monthly" price={storePrices.monthly} compact={!native} />

            <button
              onClick={() => {
                void track("upgrade_prompt_dismissed", { surface: `prompt_${reason}`, feature });
                onClose();
              }}
              className="w-full py-1 text-xs text-ink-muted underline"
            >
              Not now
            </button>
          </div>
          <p className="text-center text-[11px] text-ink-muted">
            Cancel anytime — you keep Premium until the end of your paid period.
          </p>
        </div>
      </div>
      {confirmingTrial && (
        <TrialConfirmDialog
          surface={`upgrade_prompt_${reason}`}
          onClose={() => setConfirmingTrial(false)}
          onActivated={async () => {
            await refreshPremium();
            onClose();
          }}
        />
      )}
    </div>
  );
}